/* eslint-disable @typescript-eslint/no-explicit-any */

import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import _ from 'lodash';

import { AppDispatch } from 'index';
import { Features } from '@tock/coretypes';
import { actions } from 'actions';
import rootReducer from 'state';
import type { MappedActions } from '@tock/shared/utils/connect';

type ActionsFileBundle = typeof actions;
export type ClowncarActionsType = MappedActions<ActionsFileBundle>;

export type StoreType = ReturnType<typeof rootReducer>;

export type ActionsType = ClowncarActionsType;

export interface DefaultConnectProps {
  actions: ActionsType;
  app: StoreType['app'];
  features: Features;
  locale: string;
  currency: string;
  timeZone: string;
}

type ConnectableComponent<T> =
  | React.ComponentClass<T>
  | React.FunctionComponent<React.PropsWithChildren<T>>;
interface IntrinsicAttributes {
  children?: React.ReactNode;
  ref?: string;
}

const getActions = (dispatch: Dispatch): ActionsType => {
  return _.mapValues(actions, (actionsSet: any) => {
    return bindActionCreators(actionsSet, dispatch);
  });
};

let actionsCache: {
  actions: ActionsType;
};

const getActionsMemoized = (dispatch: AppDispatch): { actions: ActionsType } => {
  if (actionsCache) {
    return actionsCache;
  }

  actionsCache = {
    actions: getActions(dispatch),
  };
  return actionsCache;
};

function mapDispatchToProps(dispatchMapper: any) {
  return function (dispatch: Dispatch) {
    const clowncarActions = getActionsMemoized(dispatch).actions;
    let customDispatch = dispatchMapper || {};
    if (_.isFunction(dispatchMapper)) {
      // eslint-disable-next-line prefer-rest-params
      customDispatch = (dispatchMapper as any)(...(arguments as any));
    }
    return {
      actions: clowncarActions,
      ...customDispatch,
    };
  };
}

type StoreKeys = Array<keyof StoreType>;
type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
type SelectorFnReturnType<Props> = Omit<Props, keyof DefaultConnectProps>;
type SelectorFn<RequiredProps, PickedConnectProps> = (
  state: StoreType,
  ownProps?: RequiredProps
) => SelectorFnReturnType<PickedConnectProps>;

export const useActions = <RequiredProps = {}, AdditionalActions extends {} = {}>(opts?: {
  dispatchMapper?: (dispatch: AppDispatch, props?: RequiredProps) => AdditionalActions;
  props?: RequiredProps;
}) => {
  const dispatchMapper = opts && opts.dispatchMapper;
  const props = opts && opts.props;

  const dispatch = useDispatch();
  if (!dispatchMapper && !props) {
    return getActionsMemoized(dispatch) as { actions: ActionsType };
  }

  const actionsOverride = (props as any).actions;

  let customDispatch = dispatchMapper || {};
  if (_.isFunction(dispatchMapper)) {
    customDispatch = dispatchMapper(dispatch, props);
  }

  return {
    actions: {
      ...getActionsMemoized(dispatch).actions,
      ...customDispatch,
      ...(actionsOverride || {}),
    } as ActionsType & AdditionalActions,
  };
};

export default function <
  RequiredProps = any,
  PickedConnectProps extends DefaultConnectProps = DefaultConnectProps,
  AdditionalActions = {},
>(
  component: ConnectableComponent<
    RequiredProps & PickedConnectProps & { actions: ActionsType & AdditionalActions }
  >,
  selector: StoreKeys | SelectorFn<RequiredProps, PickedConnectProps> = [] as StoreKeys,
  dispatchMapper?: (dispatch: Dispatch) => AdditionalActions
): React.NamedExoticComponent<RequiredProps & IntrinsicAttributes> {
  const connector = connect(
    (state: StoreType, propsFromParent: RequiredProps) => ({
      app: state.app,
      features: state.features.globalFeatures,
      locale: (propsFromParent as any)?.locale || state.app.locale,
      currency: (propsFromParent as any)?.currency || state.app.currency,
      timeZone: (propsFromParent as any)?.timeZone || state.app.timeZone,
      ...(_.isArray(selector) ? _.pick(state, selector) : selector(state, propsFromParent)),
    }),
    mapDispatchToProps(dispatchMapper)
  );

  // @ts-expect-error typing issue with redux 8
  return connector(component as any) as React.NamedExoticComponent<
    RequiredProps & IntrinsicAttributes
  >;
}
